import { useRef, useState, useEffect } from "react";
import { discontinuousTimeScaleProviderBuilder } from "@react-financial-charts/scales";
import { timeFormat } from "d3-time-format";

/** Shared plumbing for the Institutional Flows section's five tabs
 * (FlowsChart.tsx + FlowsRelativeChart/FlowsRollingCorrChart/
 * FlowsCumulativeChart/FlowsNetChart), all built on react-financial-charts
 * (Gate-0's mandated production charting library) with the same
 * discontinuous-time-scale conventions as PriceChart.tsx. Factored out here
 * rather than duplicated five times: the container-width hook, the
 * discontinuous scale builder, and the index-vs-date tick formatter (the
 * scale's ticks are row indices into plotData, not Date objects -- map back
 * before formatting, or every tick silently reads the epoch for a tiny
 * index, the same subtlety PriceChart.tsx and the original FlowsChart.tsx
 * already had to solve once each).
 */

export const CHART_HEIGHT = 160;
export const FII_COLOR = "#3467c9";
export const DII_COLOR = "#c97a1f";
export const VIX_UP_COLOR = "#b1373f";   // fear rising
export const VIX_DOWN_COLOR = "#2f8f5b"; // fear falling
export const NET_COLOR = "#6a3fa0";

export function useContainerWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setWidth(Math.floor(w));
    });
    ro.observe(el);
    setWidth(Math.floor(el.clientWidth));
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

export function buildScale<T extends { date: Date }>(data: T[]) {
  const scaleProvider = discontinuousTimeScaleProviderBuilder().inputDateAccessor((d: T) => d.date);
  const { data: plotData, xScale, xAccessor, displayXAccessor } = scaleProvider(data);
  const xExtents = [xAccessor(plotData[0]), xAccessor(plotData[plotData.length - 1])];
  const formatTickDate = (fmt: string) => {
    const f = timeFormat(fmt);
    return (index: number) => {
      const d = plotData[Math.round(index)];
      return d ? f((d as T).date) : "";
    };
  };
  return { plotData, xScale, xAccessor, displayXAccessor, xExtents, formatTickDate };
}

/** Symmetric-around-zero extent from real values only (nulls/undefined
 * never pull the range toward 0) -- shared by every tab whose chart is
 * "0-centered" by spec (all five are). */
export function symmetricExtent(values: (number | null | undefined)[], padRatio = 0.15, minPad = 0.2): [number, number] {
  const real = values.filter((v): v is number => v != null);
  const absMax = real.length ? Math.max(...real.map(Math.abs)) : 1;
  const pad = Math.max(absMax * padRatio, minPad);
  return [-(absMax + pad), absMax + pad];
}

export function NotEnoughPoints({ containerRef }: { containerRef: React.RefObject<HTMLDivElement> }) {
  return (
    <div ref={containerRef} style={{ minHeight: 40 }}>
      <div className="empty-state" style={{ padding: 8 }}>Not enough points in this window to chart.</div>
    </div>
  );
}
